import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Phone,
  Mail,
  Building2,
  Package,
  FileText,
  Truck,
  Clock,
  CalendarClock,
  MessageCircle,
  PhoneCall,
  StickyNote,
  Download,
  Send,
  CheckCircle2,
  Trash2,
  Pencil,
  Sparkles,
  Eye,
  Wand2,
  RefreshCw,
} from "lucide-react";
import { API } from "@/lib/api";
import api from "@/lib/api";
import { STAGES, SOURCE_META, PRIORITY_META, stageLabel, sourceLabel } from "@/lib/funnel";
import { StageBadge } from "@/components/StageBadge";
import EditLeadDialog from "@/components/EditLeadDialog";
import RichTextEditor from "@/components/RichTextEditor";
import WhatsAppChat from "@/components/WhatsAppChat";
import ProformaDialog from "@/components/ProformaDialog";
import QuotationDialog from "@/components/QuotationDialog";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const EVENT_ICON = {
  created: Clock,
  stage_change: CheckCircle2,
  note: StickyNote,
  call: PhoneCall,
  whatsapp: MessageCircle,
  quotation: FileText,
  sample: Package,
  courier: Truck,
  followup: CalendarClock,
  assignment: Building2,
};

function timeAgo(iso) {
  const d = new Date(iso);
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function buildEmailPreview(body, fromEmail, signatures = {}) {
  const sig = signatures[fromEmail] || "";
  return `<div style="font-family:Arial,sans-serif;font-size:14px;color:#1e293b;line-height:1.6">${body || ""}</div>${sig}`;
}

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lead, setLead] = useState(null);
  const [users, setUsers] = useState([]);
  const [note, setNote] = useState("");
  const [noteType, setNoteType] = useState("note");
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [proformaOpen, setProformaOpen] = useState(false);
  const [quoteOpenNew, setQuoteOpenNew] = useState(false);
  const [quote, setQuote] = useState({ amount: "", currency: "INR", notes: "" });
  const [senders, setSenders] = useState([]);
  const [signatures, setSignatures] = useState({});
  const [fromEmail, setFromEmail] = useState("");
  const [followOpen, setFollowOpen] = useState(false);
  const [followAt, setFollowAt] = useState("");
  const [courierOpen, setCourierOpen] = useState(false);
  const [courier, setCourier] = useState({ partner: "", tracking_number: "", status: "booked", notes: "" });
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeReason, setCloseReason] = useState("");
  const [closeStage, setCloseStage] = useState("won");
  const [editOpen, setEditOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSending, setAiSending] = useState(false);
  const [aiRefining, setAiRefining] = useState(false);
  const [aiInstruction, setAiInstruction] = useState("");
  const [aiDraft, setAiDraft] = useState({ subject: "", body: "", to_email: "" });
  const [attachProfile, setAttachProfile] = useState(true);
  const [attachCatalog, setAttachCatalog] = useState(true);
  const [aiCc, setAiCc] = useState("");
  const [aiBcc, setAiBcc] = useState("");
  const [quoteTo, setQuoteTo] = useState("");
  const [quoteCc, setQuoteCc] = useState("");
  const [quoteBcc, setQuoteBcc] = useState("");
  const [aiPreview, setAiPreview] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/leads/${id}`);
    setLead(data);
    if (data.quotation) setQuote({ amount: data.quotation.amount || "", currency: data.quotation.currency || "INR", notes: data.quotation.notes || "" });
  };

  useEffect(() => {
    load();
    api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
    api.get("/meta").then(({ data }) => {
      const list = data.sender_emails || [];
      setSenders(list);
      setSignatures(data.signatures || {});
      if (list.length) setFromEmail(list[0]);
    }).catch(() => {});
    // eslint-disable-next-line
  }, [id]);

  if (!lead) return <div className="h-64 flex items-center justify-center text-slate-400">Loading…</div>;

  const setStage = async (stage) => {
    if (stage === "won" || stage === "lost") {
      setCloseStage(stage);
      setCloseOpen(true);
      return;
    }
    await api.patch(`/leads/${id}/stage`, { stage });
    toast.success(`Stage updated to ${stageLabel(stage)}`);
    load();
  };

  const confirmClose = async () => {
    await api.patch(`/leads/${id}/stage`, { stage: closeStage, close_reason: closeReason });
    setCloseOpen(false);
    setCloseReason("");
    toast.success(`Marked as ${closeStage}`);
    load();
  };

  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/leads/${id}/notes`, { type: noteType, message: note });
    setNote("");
    toast.success("Logged");
    load();
  };

  const reassign = async (uid) => {
    await api.patch(`/leads/${id}`, { assigned_to: uid });
    toast.success("Reassigned");
    load();
  };

  const setPriority = async (priority) => {
    await api.patch(`/leads/${id}`, { priority });
    toast.success(`Priority set to ${PRIORITY_META[priority]?.label || priority}`);
    load();
  };

  const openAiReply = async () => {
    setAiOpen(true);
    setAiPreview(false);
    setAiCc("");
    setAiBcc("");
    setAiLoading(true);
    setAiDraft({ subject: "", body: "", to_email: lead.email || "" });
    try {
      const { data } = await api.post(`/leads/${id}/ai-reply/draft`);
      setAiDraft({ subject: data.subject, body: data.body, to_email: data.to_email || lead.email || "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't generate a reply");
      setAiOpen(false);
    } finally {
      setAiLoading(false);
    }
  };

  const regenerateAiReply = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post(`/leads/${id}/ai-reply/draft?force=true`);
      setAiDraft((d) => ({ ...d, subject: data.subject, body: data.body }));
      toast.success("Fresh draft generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't regenerate");
    } finally {
      setAiLoading(false);
    }
  };

  const refineAiReply = async (instruction = "") => {
    if (!aiDraft.body?.trim()) { toast.error("Write a rough draft first, then refine"); return; }
    setAiRefining(true);
    try {
      const { data } = await api.post(`/leads/${id}/ai-reply/refine`, { subject: aiDraft.subject, body: aiDraft.body, instruction });
      setAiDraft((d) => ({ ...d, subject: data.subject || d.subject, body: data.body }));
      setAiInstruction("");
      toast.success(instruction ? "Updated per your instruction" : "Polished in Aaywon's tone");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't refine the draft");
    } finally {
      setAiRefining(false);
    }
  };

  const sendAiReply = async () => {
    setAiSending(true);
    try {
      const r = await api.post(`/leads/${id}/ai-reply/send`, {
        subject: aiDraft.subject, body: aiDraft.body, from_email: fromEmail,
        to_email: aiDraft.to_email, cc: aiCc, bcc: aiBcc,
        attach_profile: attachProfile, attach_catalog: attachCatalog,
      });
      const st = r.data.email?.status;
      toast[st === "success" ? "success" : "message"](
        st === "success" ? `Reply sent to ${aiDraft.to_email}` : `Email ${st || "skipped"}`
      );
      setAiOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send reply");
    } finally {
      setAiSending(false);
    }
  };

  const saveQuote = async (sendEmail) => {
    const payload = { amount: quote.amount ? parseFloat(quote.amount) : null, currency: quote.currency, notes: quote.notes, mark_sent: true };
    if (sendEmail) {
      const { data } = await api.post(`/leads/${id}/quotation`, payload);
      const r = await api.post(`/leads/${id}/quotation/email`, { from_email: fromEmail, to_email: quoteTo || lead.email, cc: quoteCc, bcc: quoteBcc });
      const st = r.data.email?.status;
      toast[st === "success" ? "success" : "message"](
        st === "success" ? `Quotation emailed from ${fromEmail}` : `Quotation saved (email ${st || "skipped"})`
      );
    } else {
      await api.post(`/leads/${id}/quotation`, payload);
      toast.success("Quotation saved & marked sent");
    }
    setQuoteOpen(false);
    load();
  };

  const downloadPdf = () => {
    window.open(`${API}/leads/${id}/quotation/pdf`, "_blank");
  };

  const saveFollow = async () => {
    if (!followAt) return;
    await api.post(`/leads/${id}/followup`, { next_follow_up_at: new Date(followAt).toISOString() });
    setFollowOpen(false);
    toast.success("Follow-up scheduled");
    load();
  };

  const markSample = async (sent) => {
    await api.post(`/leads/${id}/sample`, sent ? { sent: true } : { required: true });
    toast.success(sent ? "Sample marked sent" : "Sample marked required");
    load();
  };

  const saveCourier = async () => {
    await api.post(`/leads/${id}/courier`, courier);
    setCourierOpen(false);
    toast.success("Courier updated");
    load();
  };

  const removeLead = async () => {
    await api.delete(`/leads/${id}`);
    toast.success("Lead deleted");
    navigate("/leads");
  };

  const src = SOURCE_META[lead.source] || {};
  const canManage = ["owner", "manager"].includes(user?.role);

  return (
    <div data-testid="lead-detail-page" className="max-w-6xl mx-auto">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-display text-2xl font-semibold text-slate-900">{lead.company_name}</h1>
            <StageBadge stage={lead.stage} />
            <span data-testid="priority-badge" className={`text-xs px-2 py-0.5 rounded font-semibold ${PRIORITY_META[lead.priority]?.color || PRIORITY_META.medium.color}`}>
              {PRIORITY_META[lead.priority]?.label || "Medium"}
            </span>
            {lead.overdue && <span className="text-xs px-2 py-0.5 rounded font-bold bg-red-100 text-red-700">Overdue · {lead.hours_overdue}h</span>}
          </div>
          <div className="flex items-center gap-3 mt-2 text-sm text-slate-500 flex-wrap">
            <span className={`text-xs px-2 py-0.5 rounded font-medium ${src.color}`}>{sourceLabel(lead)}</span>
            {lead.contact_name && <span className="flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{lead.contact_name}</span>}
            {lead.phone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{lead.phone}</span>}
            {lead.email && <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{lead.email}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={lead.priority || "medium"} onValueChange={setPriority}>
            <SelectTrigger className="w-32 bg-white" data-testid="priority-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(PRIORITY_META).map(([val, meta]) => (
                <SelectItem key={val} value={val} data-testid={`priority-option-${val}`}>{meta.label} Priority</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={lead.stage} onValueChange={setStage}>
            <SelectTrigger className="w-44 bg-white" data-testid="stage-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STAGES.map((s) => <SelectItem key={s} value={s}>{stageLabel(s)}</SelectItem>)}
            </SelectContent>
          </Select>
          {canManage && (
            <Button variant="outline" size="icon" onClick={() => setEditOpen(true)} data-testid="edit-lead-btn" title="Edit lead">
              <Pencil className="h-4 w-4" />
            </Button>
          )}
          {(canManage || !!user?.can_delete) && (
            <Button variant="outline" size="icon" onClick={removeLead} data-testid="delete-lead-btn" className="text-red-600">
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Fast actions */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 mb-6">
        <FastAction icon={Sparkles} label="AI Reply" onClick={openAiReply} testid="action-ai-reply" />
        <FastAction icon={CheckCircle2} label="Mark Contacted" onClick={() => setStage("contacted")} testid="action-contacted" />
        <FastAction icon={Send} label="Send Quotation" onClick={() => { setQuoteTo(lead.email || ""); setQuoteOpen(true); }} testid="action-quotation" />
        <FastAction icon={CalendarClock} label="Add Follow-up" onClick={() => setFollowOpen(true)} testid="action-followup" />
        <FastAction icon={Package} label="Sample Sent" onClick={() => markSample(true)} testid="action-sample" />
        <FastAction icon={Truck} label="Update Courier" onClick={() => setCourierOpen(true)} testid="action-courier" />
        <FastAction icon={Download} label="Quote PDF" onClick={downloadPdf} testid="action-pdf" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: details + activity */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-medium">Requirement</h3>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => setQuoteOpenNew(true)} data-testid="quotation-btn" className="h-8 gap-1.5 border-rose-200 text-rose-700 hover:bg-rose-50">
                  <FileText className="h-3.5 w-3.5" /> Quotation
                </Button>
                <Button size="sm" variant="outline" onClick={() => setProformaOpen(true)} data-testid="proforma-btn" className="h-8 gap-1.5 border-rose-200 text-rose-700 hover:bg-rose-50">
                  <FileText className="h-3.5 w-3.5" /> Proforma Invoice
                </Button>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <Field label="Product" value={lead.product_requirement} />
              <Field label="Quantity" value={lead.quantity} />
              <Field label="Diameter (Dia)" value={lead.diameter} />
              <Field label="Tube Type" value={lead.tube_type} />
              <Field label="City / Location" value={lead.city} />
              <Field label="Est. Value" value={lead.estimated_value ? `₹${lead.estimated_value.toLocaleString()}` : null} />
              <Field label="Priority" value={PRIORITY_META[lead.priority]?.label} />
              <div className="sm:col-span-2">
                <Field label="Specifications" value={lead.specifications} />
              </div>
            </div>
          </div>

          {/* Log note */}
          <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
            <h3 className="font-display text-lg font-medium mb-3">Log Communication</h3>
            <div className="flex gap-2 mb-2">
              {[["note", StickyNote, "Note"], ["call", PhoneCall, "Call"], ["whatsapp", MessageCircle, "WhatsApp"]].map(([t, Ic, lbl]) => (
                <button
                  key={t}
                  onClick={() => setNoteType(t)}
                  data-testid={`note-type-${t}`}
                  className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md font-medium ${noteType === t ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600"}`}
                >
                  <Ic className="h-3.5 w-3.5" /> {lbl}
                </button>
              ))}
            </div>
            <Textarea
              data-testid="note-input"
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={`Add a ${noteType} note…`}
            />
            <div className="flex justify-end mt-2">
              <Button onClick={addNote} data-testid="add-note-btn" className="bg-slate-800 hover:bg-slate-900">Log</Button>
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
            <h3 className="font-display text-lg font-medium mb-4">Activity Timeline</h3>
            <div className="space-y-0">
              {(lead.events || []).map((e, i) => {
                const Ic = EVENT_ICON[e.type] || Clock;
                return (
                  <div key={e.id} className="flex gap-3 pb-4 relative" data-testid={`timeline-event-${i}`}>
                    {i !== (lead.events.length - 1) && (
                      <span className="absolute left-[15px] top-8 bottom-0 w-px bg-slate-200" />
                    )}
                    <div className="h-8 w-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
                      <Ic className="h-4 w-4 text-slate-500" />
                    </div>
                    <div className="pt-1">
                      <p className="text-sm text-slate-800">{e.message}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{e.user_name} · {timeAgo(e.created_at)}</p>
                    </div>
                  </div>
                );
              })}
              {(!lead.events || lead.events.length === 0) && (
                <p className="text-sm text-slate-400">No activity yet.</p>
              )}
            </div>
          </div>
        </div>

        {/* Right: ownership + cards */}
        <div className="space-y-6">
          {lead.phone && (
            <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="lead-whatsapp-card">
              <h3 className="font-display text-lg font-medium mb-3 flex items-center gap-2">
                <MessageCircle className="h-5 w-5 text-emerald-600" /> WhatsApp
              </h3>
              <WhatsAppChat leadId={id} heightClass="h-[320px]" />
            </div>
          )}
          <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
            <h3 className="font-display text-lg font-medium mb-3">Ownership</h3>
            <Label className="text-xs">Assigned To</Label>
            <Select value={lead.assigned_to || "unassigned"} onValueChange={(v) => reassign(v === "unassigned" ? null : v)}>
              <SelectTrigger data-testid="reassign-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}
              </SelectContent>
            </Select>
            {lead.next_follow_up_at && (
              <p className="mt-3 text-sm text-slate-600 flex items-center gap-1.5">
                <CalendarClock className="h-4 w-4 text-amber-500" /> Follow-up: {timeAgo(lead.next_follow_up_at)}
              </p>
            )}
          </div>

          <StatusCard title="Quotation" icon={FileText}>
            {lead.quotation?.sent_at ? (
              <>
                <p className="text-sm text-slate-700">{lead.quotation.currency} {Number(lead.quotation.amount || 0).toLocaleString()}</p>
                <p className="text-xs text-slate-400">Sent {timeAgo(lead.quotation.sent_at)} · {lead.quotation.quote_number}</p>
                <button onClick={downloadPdf} className="text-xs text-blue-600 hover:underline mt-1 flex items-center gap-1"><Download className="h-3 w-3" /> Download PDF</button>
              </>
            ) : (
              <p className="text-sm text-slate-400">Not sent yet</p>
            )}
          </StatusCard>

          <StatusCard title="Sample" icon={Package}>
            {lead.sample?.sent ? (
              <p className="text-sm text-green-700">Sent {timeAgo(lead.sample.dispatched_at)}</p>
            ) : lead.sample?.required ? (
              <p className="text-sm text-amber-700">Required — not sent</p>
            ) : (
              <p className="text-sm text-slate-400">Not requested</p>
            )}
          </StatusCard>

          <StatusCard title="Courier" icon={Truck}>
            {lead.courier?.tracking_number ? (
              <>
                <p className="text-sm text-slate-700">{lead.courier.partner} · {lead.courier.tracking_number}</p>
                <p className="text-xs capitalize text-slate-500">{(lead.courier.status || "").replace("_", " ")}</p>
              </>
            ) : (
              <p className="text-sm text-slate-400">No courier info</p>
            )}
          </StatusCard>
        </div>
      </div>

      {/* Quotation dialog */}
      <Dialog open={quoteOpen} onOpenChange={setQuoteOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="quotation-dialog">
          <DialogHeader><DialogTitle className="font-display">Quotation</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">Amount</Label>
                <Input data-testid="quote-amount" type="number" value={quote.amount} onChange={(e) => setQuote({ ...quote, amount: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Currency</Label>
                <Input value={quote.currency} onChange={(e) => setQuote({ ...quote, currency: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea rows={2} value={quote.notes} onChange={(e) => setQuote({ ...quote, notes: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input data-testid="quote-to" value={quoteTo} onChange={(e) => setQuoteTo(e.target.value)} placeholder="buyer@email.com" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">CC</Label>
                <Input data-testid="quote-cc" value={quoteCc} onChange={(e) => setQuoteCc(e.target.value)} placeholder="comma separated" />
              </div>
              <div>
                <Label className="text-xs">BCC</Label>
                <Input data-testid="quote-bcc" value={quoteBcc} onChange={(e) => setQuoteBcc(e.target.value)} placeholder="comma separated" />
              </div>
            </div>
            {senders.length > 0 && (
              <div>
                <Label className="text-xs">Send from</Label>
                <Select value={fromEmail} onValueChange={setFromEmail}>
                  <SelectTrigger data-testid="quote-from-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {senders.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => saveQuote(false)} data-testid="quote-save-btn">Mark Sent</Button>
            <Button onClick={() => saveQuote(true)} data-testid="quote-email-btn" className="bg-blue-600 hover:bg-blue-700">
              <Send className="h-4 w-4 mr-1.5" /> Email Quotation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Follow-up dialog */}
      <Dialog open={followOpen} onOpenChange={setFollowOpen}>
        <DialogContent data-testid="followup-dialog">
          <DialogHeader><DialogTitle className="font-display">Schedule Follow-up</DialogTitle></DialogHeader>
          <div className="py-2">
            <Label className="text-xs">Date & Time</Label>
            <Input data-testid="followup-datetime" type="datetime-local" value={followAt} onChange={(e) => setFollowAt(e.target.value)} />
          </div>
          <DialogFooter>
            <Button onClick={saveFollow} data-testid="followup-save-btn" className="bg-blue-600 hover:bg-blue-700">Schedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Courier dialog */}
      <Dialog open={courierOpen} onOpenChange={setCourierOpen}>
        <DialogContent data-testid="courier-dialog">
          <DialogHeader><DialogTitle className="font-display">Update Courier</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Partner</Label>
                <Input data-testid="courier-partner" value={courier.partner} onChange={(e) => setCourier({ ...courier, partner: e.target.value })} placeholder="DTDC / Bluedart" />
              </div>
              <div>
                <Label className="text-xs">Tracking #</Label>
                <Input data-testid="courier-tracking" value={courier.tracking_number} onChange={(e) => setCourier({ ...courier, tracking_number: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={courier.status} onValueChange={(v) => setCourier({ ...courier, status: v })}>
                <SelectTrigger data-testid="courier-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="booked">Booked</SelectItem>
                  <SelectItem value="in_transit">In Transit</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveCourier} data-testid="courier-save-btn" className="bg-blue-600 hover:bg-blue-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close dialog */}
      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent data-testid="close-dialog">
          <DialogHeader><DialogTitle className="font-display">Mark as {closeStage === "won" ? "Won" : "Lost"}</DialogTitle></DialogHeader>
          <div className="py-2">
            <Label className="text-xs">Reason / Notes</Label>
            <Textarea data-testid="close-reason" rows={2} value={closeReason} onChange={(e) => setCloseReason(e.target.value)} />
          </div>
          <DialogFooter>
            <Button onClick={confirmClose} data-testid="close-confirm-btn" className={closeStage === "won" ? "bg-green-600 hover:bg-green-700" : "bg-slate-700 hover:bg-slate-800"}>
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EditLeadDialog open={editOpen} onOpenChange={setEditOpen} lead={lead} onSaved={load} />

      <ProformaDialog lead={lead} open={proformaOpen} onOpenChange={setProformaOpen} />
      <QuotationDialog lead={lead} open={quoteOpenNew} onOpenChange={setQuoteOpenNew} />

      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="ai-reply-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-blue-600" /> AI Draft Reply
            </DialogTitle>
          </DialogHeader>
          {aiLoading ? (
            <div className="py-12 text-center text-slate-500 text-sm flex flex-col items-center gap-2">
              <Sparkles className="h-6 w-6 text-blue-500 animate-pulse" />
              Reading the enquiry and drafting a reply…
            </div>
          ) : aiPreview ? (
            <div className="space-y-2 py-1" data-testid="ai-reply-preview">
              <div className="text-xs text-slate-500 space-y-0.5 border-b border-slate-100 pb-2">
                <p><span className="font-semibold text-slate-600">From:</span> {fromEmail}</p>
                <p><span className="font-semibold text-slate-600">To:</span> {aiDraft.to_email || "—"}</p>
                {aiCc && <p><span className="font-semibold text-slate-600">Cc:</span> {aiCc}</p>}
                {aiBcc && <p><span className="font-semibold text-slate-600">Bcc:</span> {aiBcc}</p>}
                <p><span className="font-semibold text-slate-600">Subject:</span> {aiDraft.subject}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white p-4 max-h-[40vh] overflow-y-auto"
                   dangerouslySetInnerHTML={{ __html: buildEmailPreview(aiDraft.body, fromEmail, signatures) }} />
              {(attachProfile || attachCatalog) && (
                <p className="text-xs text-slate-500 flex flex-wrap gap-2">
                  <span className="font-semibold text-slate-600">Attachments:</span>
                  {attachProfile && <span className="rounded bg-slate-100 px-1.5 py-0.5">Company Profile.pdf</span>}
                  {attachCatalog && <span className="rounded bg-slate-100 px-1.5 py-0.5">Product Catalog.pdf</span>}
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-3 py-1">
              <div>
                <Label className="text-xs">To</Label>
                <Input data-testid="ai-reply-to" value={aiDraft.to_email} onChange={(e) => setAiDraft({ ...aiDraft, to_email: e.target.value })} placeholder="buyer@email.com" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">CC</Label>
                  <Input data-testid="ai-reply-cc" value={aiCc} onChange={(e) => setAiCc(e.target.value)} placeholder="comma separated" />
                </div>
                <div>
                  <Label className="text-xs">BCC</Label>
                  <Input data-testid="ai-reply-bcc" value={aiBcc} onChange={(e) => setAiBcc(e.target.value)} placeholder="comma separated" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Subject</Label>
                <Input data-testid="ai-reply-subject" value={aiDraft.subject} onChange={(e) => setAiDraft({ ...aiDraft, subject: e.target.value })} />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs">Message (review & edit before sending)</Label>
                  <div className="flex items-center gap-1.5">
                    <button type="button" onClick={refineAiReply} disabled={aiRefining} data-testid="ai-reply-refine" className="text-[11px] font-medium text-violet-600 hover:text-violet-800 disabled:opacity-50 flex items-center gap-1">
                      <Wand2 className="h-3.5 w-3.5" /> {aiRefining ? "Refining…" : "Refine with AI"}
                    </button>
                    <span className="w-px h-3.5 bg-slate-200" />
                    <button type="button" onClick={regenerateAiReply} disabled={aiLoading} data-testid="ai-reply-regenerate" className="text-[11px] font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50 flex items-center gap-1">
                      <RefreshCw className="h-3.5 w-3.5" /> Regenerate
                    </button>
                  </div>
                </div>
                <RichTextEditor testid="ai-reply-body" value={aiDraft.body} onChange={(html) => setAiDraft((d) => ({ ...d, body: html }))} />
                <p className="text-[11px] text-slate-400 mt-1">Tip: write your own rough notes and hit "Refine with AI" to polish it in Aaywon's tone.</p>
              </div>

              <div className="rounded-lg border border-violet-200 bg-violet-50/60 p-3 space-y-2" data-testid="ai-instruction-box">
                <p className="text-xs font-semibold text-violet-700 flex items-center gap-1"><Wand2 className="h-3.5 w-3.5" /> Tell AI what to change</p>
                <div className="flex flex-wrap gap-1.5">
                  {["Make it shorter", "More formal", "Friendlier tone", "Add a clear call-to-action", "Ask for tube specs"].map((chip) => (
                    <button
                      key={chip}
                      type="button"
                      onClick={() => refineAiReply(chip)}
                      disabled={aiRefining}
                      data-testid={`ai-chip-${chip.replace(/\s+/g, "-").toLowerCase()}`}
                      className="text-[11px] px-2 py-1 rounded-full bg-white border border-violet-200 text-violet-700 hover:bg-violet-100 disabled:opacity-50 transition-colors"
                    >
                      {chip}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    value={aiInstruction}
                    onChange={(e) => setAiInstruction(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && aiInstruction.trim()) { e.preventDefault(); refineAiReply(aiInstruction.trim()); } }}
                    placeholder="e.g. keep it under 80 words and mention 15-day lead time"
                    data-testid="ai-instruction-input"
                    className="h-9 text-sm bg-white"
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => refineAiReply(aiInstruction.trim())}
                    disabled={aiRefining || !aiInstruction.trim()}
                    data-testid="ai-instruction-apply"
                    className="bg-violet-600 hover:bg-violet-700 shrink-0"
                  >
                    {aiRefining ? "Applying…" : "Apply"}
                  </Button>
                </div>
              </div>
              {senders.length > 0 && (
                <div>
                  <Label className="text-xs">Send from</Label>
                  <Select value={fromEmail} onValueChange={setFromEmail}>
                    <SelectTrigger data-testid="ai-reply-from"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {senders.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
                <p className="text-xs font-semibold text-slate-600">Attachments</p>
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input type="checkbox" data-testid="ai-attach-profile" checked={attachProfile} onChange={(e) => setAttachProfile(e.target.checked)} className="h-4 w-4 accent-blue-600" />
                  Company Profile (Aaywon Brochure)
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input type="checkbox" data-testid="ai-attach-catalog" checked={attachCatalog} onChange={(e) => setAttachCatalog(e.target.checked)} className="h-4 w-4 accent-blue-600" />
                  Product Catalog
                </label>
              </div>
              {!aiDraft.to_email && (
                <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  This lead has no email address (common for IndiaMART direct enquiries). Add an email via Edit to send, or copy the draft to reply via WhatsApp/IndiaMART chat.
                </p>
              )}
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {!aiLoading && (
              <Button variant="outline" data-testid="ai-reply-preview-toggle" onClick={() => setAiPreview((p) => !p)} disabled={!aiDraft.body} className="sm:mr-auto">
                <Eye className="h-4 w-4 mr-1.5" /> {aiPreview ? "Back to edit" : "Preview"}
              </Button>
            )}
            <Button variant="outline" onClick={() => navigator.clipboard.writeText(aiDraft.body).then(() => toast.success("Draft copied"))} disabled={aiLoading || !aiDraft.body}>Copy</Button>
            <Button data-testid="ai-reply-send-btn" onClick={sendAiReply} disabled={aiLoading || aiSending || !aiDraft.to_email} className="bg-blue-600 hover:bg-blue-700">
              {aiSending ? "Sending…" : "Send Reply"}
            </Button>

          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const FastAction = ({ icon: Icon, label, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="flex flex-col items-center justify-center gap-1.5 bg-white border border-slate-200 rounded-lg py-3 px-2 shadow-sm hover:shadow-md hover:border-blue-300 hover:-translate-y-0.5 transition-all"
  >
    <Icon className="h-5 w-5 text-blue-600" />
    <span className="text-xs font-medium text-slate-700 text-center leading-tight">{label}</span>
  </button>
);

const Field = ({ label, value }) => (
  <div>
    <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">{label}</p>
    <p className="text-slate-800 mt-0.5">{value || "—"}</p>
  </div>
);

const StatusCard = ({ title, icon: Icon, children }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
    <div className="flex items-center gap-2 mb-2">
      <Icon className="h-4 w-4 text-slate-500" />
      <h3 className="font-display text-base font-medium">{title}</h3>
    </div>
    {children}
  </div>
);
